import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';
import { SecureStorageService } from './core/services/secure-storage.service';
import { RoutePrefetchService } from './core/services/route-prefetch.service';
import { AuthService } from './features/auth/services/auth.service';
import { environment } from '../environments/environment';
import { BUILD_INFO } from '../environments/version';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HttpClientModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Luqta Admin';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private secureStorage: SecureStorageService,
    private router: Router,
    private authService: AuthService,
    private routePrefetch: RoutePrefetchService
  ) {}

  ngOnInit(): void {
    // Prints on every load so you can confirm in DevTools whether a deploy
    // actually reflects the latest build (dev vs prod, version/time).
    console.log(
      `%c[Luqta Admin] ${environment.production ? 'PROD' : 'DEV'} build ${BUILD_INFO.version} (built ${BUILD_INFO.buildTime})`,
      'color:#ffffff;background:#5304fb;font-weight:bold;padding:4px 8px;border-radius:4px;'
    );

    // Migrate existing unencrypted data to encrypted format
    this.secureStorage.migrateToEncrypted();

    // Re-arm session idle/absolute timers if a token already exists
    // (e.g. a hard page refresh, where login() never re-runs).
    this.authService.initSessionIfAuthenticated();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        // Inside the app (not on the login screen) → warm up the lazy sidebar
        // bundles in the background, so a later click opens the page at once.
        // start() is a no-op after the first call.
        if (!event.urlAfterRedirects.startsWith('/auth')) {
          this.routePrefetch.start();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
